import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { Alert, AlertDescription } from '../ui/alert'
import { useAuth } from '../../lib/auth.context'
import { useToast } from '../../hooks/use-toast'
import { supabase } from '../../lib/supabase'
import { Session } from '@supabase/supabase-js'

interface TOTPVerifyProps {
  onComplete?: () => void
  redirectTo?: string
}

export function TOTPVerify({ onComplete, redirectTo }: TOTPVerifyProps) {
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const { setIsTOTPVerified } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  useEffect(() => {
    const getTOTPFactor = async () => {
      try {
        const { data: factors } = await supabase.auth.mfa.listFactors()
        if (!factors?.totp?.length) throw new Error('No TOTP factors found')

        const totpFactor = factors.totp[0]
        if (!totpFactor) throw new Error('No verified TOTP factor found')

        setFactorId(totpFactor.id)
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to get TOTP factor'
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    getTOTPFactor()
  }, [toast])

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      if (!factorId) throw new Error('TOTP factor not found')

      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId
      })

      if (error) throw error

      const { data, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode
      })

      if (verifyError) throw verifyError

      console.log('[TOTPVerify] MFA verification successful, data:', {
        hasAccessToken: !!data.access_token,
        hasRefreshToken: !!data.refresh_token,
        userId: data.user?.id
      })

      // Create a new session object from the response
      const session: Session = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
        expires_at: Math.floor(Date.now() / 1000) + data.expires_in,
        token_type: data.token_type,
        user: data.user
      }

      console.log('[TOTPVerify] Setting session with MFA verification complete')

      // Set the new session with aal2 token
      const { error: sessionError } = await supabase.auth.setSession(session)

      if (sessionError) {
        console.error('[TOTPVerify] Error setting session:', sessionError)
        throw sessionError
      }

      // Verify the session was set correctly
      const { data: currentSession } = await supabase.auth.getSession()
      console.log('[TOTPVerify] Current session after setSession:', {
        hasSession: !!currentSession.session,
        userId: currentSession.session?.user?.id
      })

      // Manually set TOTP verification state to ensure it's set immediately
      console.log('[TOTPVerify] Manually setting TOTP verification to true')
      setIsTOTPVerified(true)

      toast({
        title: 'Success',
        description: 'TOTP verification successful',
      })

      // Get user profile to check role
      const userProfile = await getUserProfile()
      const isPartnerUser = userProfile.userRole?.startsWith('entity_')

      if (onComplete) {
        onComplete()
      }

      // Add a small delay to ensure auth context is updated before redirect
      setTimeout(() => {
        console.log('[TOTPVerify] Proceeding with redirect after delay')
        // Since this is partner-ui, redirect to the dashboard
        if (redirectTo?.startsWith('http')) {
          // Handle any external URLs
          window.location.replace(redirectTo)
        } else if (redirectTo) {
          // Internal route navigation
          navigate(redirectTo)
        } else {
          // Default internal route for partner users
          navigate(ROUTES.ADMIN.DASHBOARD)
        }
      }, 200)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
    setVerifyCode(value)
  }

 return (
     <div className="flex h-screen w-full items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
       <Card className="w-full max-w-md p-6">
         <h1 className="mb-6 text-2xl font-bold text-center">
           Two-Factor Authentication
         </h1>
         <form onSubmit={handleVerify} className="space-y-4">
           <div>
             <Input
               type="text"
               inputMode="numeric"
               pattern="[0-9]*"
               maxLength={6}
               placeholder="Enter verification code"
               value={verifyCode}
               onChange={handleCodeChange}
               required
               disabled={isLoading}
               className="w-full"
             />
           </div>
           {formError && (
             <Alert variant="destructive">
               <AlertDescription>{formError}</AlertDescription>
             </Alert>
           )}
           <Button
             type="submit"
             className="w-full"
             disabled={isLoading}
           >
             {isLoading ? 'Verifying...' : 'Verify'}
           </Button>
         </form>
       </Card>
     </div>
   )
}
